import React, { useState, useContext, useEffect } from "react";
import { ScreenSizeContext } from "../context/ScreenSizeContext";
import Presentation from "./Presentation";
import Experiences from "./Experiences";
import Hobbies from "./Hobbies";
import Roles from "./Roles";
import Skills from "./Skills";
import Languages from "./Languages";
import Educations from "./Educations";
import { AuthContext } from "../context/AuthContext";
import UserInfo from "./UserInfo";
import ProfileTitle from "./ProfileTitle";
import ConfirmDialog from "./ConfirmDialog";
import { useSnackbar } from "notistack";
import EditIcon from "@material-ui/icons/Edit";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import getProfilePictureSource from "../genericFunctions/getProfilePictureSource";
import getUserFriendlyMessages from "../genericFunctions/getUserFriendlyMessages";
import getDisplayTranslation from "../genericFunctions/getDisplayTranslation";
import {
  PROFILE_LANGUAGES,
  getProfileLanguageName,
} from "../genericFunctions/profileLanguages";
import {
  CREATED,
  REMOVED,
  UPDATED,
  UPLOADED,
} from "../genericFunctions/operationTypeConstants";
import {
  EDUCATIONS,
  EXPERIENCES,
  HOBBIES,
  LANGUAGES,
  SKILLS,
  ROLES,
} from "../services/serviceConstants";

// Shared profile editor. The profileService prop decides whose profile is edited.
// A profile exists in zero or more languages; each language is a complete,
// independently edited translation.
const ProfileEditor = (props) => {
  const profileService = props.profileService;
  const { onMobile } = useContext(ScreenSizeContext);
  const authContext = useContext(AuthContext);
  const [profilePic, setProfilePic] = useState({ profilePic: "" });
  const [userProfilePic, setUserProfilePic] = useState("placeholder.PNG");
  const [pictureHovered, setPictureHovered] = useState(false);
  const [message, setMessage] = useState(null);
  const [consultant, setConsultant] = useState({});
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [addLanguageOpen, setAddLanguageOpen] = useState(false);
  const [languageToAdd, setLanguageToAdd] = useState("");
  const [seedFromSelected, setSeedFromSelected] = useState(true);
  const [creatingTranslation, setCreatingTranslation] = useState(false);
  const [deleteLanguageOpen, setDeleteLanguageOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const translations = consultant.translations || [];
  const selectedTranslation = translations.find(
    (translation) => translation.language === selectedLanguage
  );
  const missingLanguages = PROFILE_LANGUAGES.filter(
    (language) =>
      !translations.some((translation) => translation.language === language)
  );

  const onSubmit = () => {
    const formData = new FormData();
    formData.append("profilePic", profilePic.profilePic);
    profileService.uploadProfilePic(formData).then((data) => {
      const { message } = data;
      if (!message.msgError) {
        profileService.getProfilePic().then((data) => {
          setUserProfilePic(data.profilePic);
          setMessage(getUserFriendlyMessages(UPLOADED, "profile picture"));
        });
      } else if (message.msgBody === "Unauthorized") {
        setMessage(message);
        authContext.setUser({ username: "", role: "" });
        authContext.setIsAuthenticated(false);
      } else {
        setMessage(message);
      }
    });
  };

  const changeProfilePic = (e) => {
    setProfilePic({ profilePic: e.target.files[0] });
  };

  useEffect(() => {
    if (message !== null) {
      enqueueSnackbar(message.msgBody, {
        variant: message.msgError ? "error" : "info",
      });
    }
    setTimeout(() => {
      setMessage(null);
    }, 3000);
  }, [message]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (profilePic.profilePic !== "") {
      onSubmit();
    }
  }, [profilePic.profilePic]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateConsultant = () => {
    return profileService.getConsultant().then((data) => {
      if (data && data.user) {
        setConsultant(data.user);
        if (props.onConsultantLoaded) {
          props.onConsultantLoaded(data.user);
        }
      }
    });
  };
  useEffect(() => {
    updateConsultant();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const displayTranslation = getDisplayTranslation(
      consultant.translations,
      selectedLanguage
    );
    setSelectedLanguage(displayTranslation ? displayTranslation.language : null);
  }, [consultant]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    profileService.getProfilePic().then((data) => {
      if (data.profilePic) {
        setUserProfilePic(data.profilePic);
      }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const displayMessage = (message, messageToDisplayOnSuccess) => {
    if (!message.msgError) {
      setMessage(messageToDisplayOnSuccess);
    } else if (message.msgBody === "Unauthorized") {
      setMessage(message);
      authContext.setUser({ username: "", role: "" });
      authContext.setIsAuthenticated(false);
    } else {
      setMessage(message);
    }
  };

  const displayError = (err) =>
    displayMessage(
      (err && err.data && err.data.message) || {
        msgBody: "Unauthorized",
        msgError: true,
      }
    );

  const saveUserInfo = (userInfo) => {
    return profileService
      .updateUserInfo(userInfo)
      .then((data) => {
        displayMessage(
          data.message,
          getUserFriendlyMessages(UPDATED, "user information")
        );
        return updateConsultant();
      })
      .catch((err) => {
        displayError(err);
        throw err;
      });
  };

  const changePrivateResumeStatus = (privateResume) => {
    const revert = () =>
      setConsultant((prev) => ({ ...prev, privateResume: !privateResume }));
    setConsultant((prev) => ({ ...prev, privateResume: privateResume }));
    profileService
      .updatePrivateResumeStatus(privateResume)
      .then((data) => {
        displayMessage(
          data.message,
          getUserFriendlyMessages(UPDATED, "private CV value")
        );
        if (data.message.msgError) {
          revert();
        }
      })
      .catch((err) => {
        revert();
        displayError(err);
      });
  };

  const changePublishedStatus = (published) => {
    const translationId = selectedTranslation._id;
    const apply = (value) =>
      setConsultant((prev) => ({
        ...prev,
        translations: (prev.translations || []).map((translation) =>
          translation._id === translationId
            ? { ...translation, published: value }
            : translation
        ),
      }));
    apply(published);
    profileService
      .updateTranslationInfo(translationId, { published: published })
      .then((data) => {
        displayMessage(
          data.message,
          getUserFriendlyMessages(UPDATED, "editing value")
        );
        if (data.message.msgError) {
          apply(!published);
        }
      })
      .catch((err) => {
        apply(!published);
        displayError(err);
      });
  };

  const createTranslation = (language, seedFromLanguage) => {
    setCreatingTranslation(true);
    profileService
      .createTranslation(language, seedFromLanguage)
      .then((data) => {
        displayMessage(
          data.message,
          getUserFriendlyMessages(
            CREATED,
            `${getProfileLanguageName(language)} profile`
          )
        );
        if (!data.message.msgError) {
          setAddLanguageOpen(false);
          return updateConsultant().then(() => setSelectedLanguage(language));
        }
      })
      .catch(displayError)
      .finally(() => setCreatingTranslation(false));
  };

  const deleteTranslation = () => {
    setDeleteLanguageOpen(false);
    profileService
      .deleteTranslation(selectedTranslation._id)
      .then((data) => {
        displayMessage(
          data.message,
          getUserFriendlyMessages(
            REMOVED,
            `${getProfileLanguageName(selectedLanguage)} profile`
          )
        );
        updateConsultant();
      })
      .catch(displayError);
  };

  const openAddLanguage = () => {
    setLanguageToAdd(missingLanguages[0] || "");
    setSeedFromSelected(true);
    setAddLanguageOpen(true);
  };

  const onSubmitTitle = (e, title) => {
    e.preventDefault();
    profileService
      .updateTranslationInfo(selectedTranslation._id, { title: title })
      .then((data) => {
        displayMessage(data.message, getUserFriendlyMessages(UPDATED, "title"));
        updateConsultant();
      })
      .catch(displayError);
  };

  const onSubmitPresentation = (e, presentation) => {
    e.preventDefault();
    profileService
      .updateTranslationInfo(selectedTranslation._id, {
        presentation: presentation.presentation,
      })
      .then((data) => {
        displayMessage(
          data.message,
          getUserFriendlyMessages(UPDATED, "presentation text")
        );
        updateConsultant();
      })
      .catch(displayError);
  };

  const onSubmitRole = (e, role) => {
    createObject(e, role, ROLES);
  };
  const handleRemoveRole = (role) => {
    handleRemove(role._id, ROLES);
  };
  const updateRole = (e, role) => {
    saveObject(e, role, ROLES);
  };

  const onSubmitSkills = (e, skill) => {
    createObject(e, skill, SKILLS);
  };
  const handleRemoveSkill = (skill) => {
    handleRemove(skill._id, SKILLS);
  };
  const updateSkill = (e, skill) => {
    saveObject(e, skill, SKILLS);
  };
  const onSubmitHobbies = (e, hobby) => {
    createObject(e, hobby, HOBBIES);
  };
  const handleRemoveHobby = (hobby) => {
    handleRemove(hobby._id, HOBBIES);
  };
  const onSubmitLanguages = (e, language) => {
    createObject(e, language, LANGUAGES);
  };

  const handleRemoveLanguage = (language) => {
    handleRemove(language._id, LANGUAGES);
  };
  const updateLanguage = (e, language) => {
    saveObject(e, language, LANGUAGES);
  };

  const onSubmitEducations = (e, education) => {
    createObject(e, education, EDUCATIONS);
  };

  const handleRemoveEducation = (education) => {
    handleRemove(education._id, EDUCATIONS);
  };

  const updateEducation = (e, education) => {
    saveObject(e, education, EDUCATIONS);
  };

  const onSubmitExperiences = (e, experience) => {
    createObject(e, experience, EXPERIENCES);
  };

  const handleRemoveExperience = (experience) => {
    handleRemove(experience._id, EXPERIENCES);
  };

  const updateExperience = (e, experience) => {
    saveObject(e, experience, EXPERIENCES);
  };

  const createObject = (e, object, collectionName) => {
    e.preventDefault();
    profileService
      .createTranslationItem(selectedTranslation._id, object, collectionName)
      .then((data) => {
        displayMessage(
          data.message,
          getUserFriendlyMessages(CREATED, collectionName)
        );
        updateConsultant();
      })
      .catch(displayError);
  };
  const saveObject = (e, object, collectionName) => {
    e.preventDefault();
    profileService
      .updateTranslationItem(selectedTranslation._id, object, collectionName)
      .then((data) => {
        displayMessage(
          data.message,
          getUserFriendlyMessages(UPDATED, collectionName)
        );
        updateConsultant();
      })
      .catch(displayError);
  };

  const handleRemove = (objectId, collectionName) => {
    profileService
      .deleteTranslationItem(selectedTranslation._id, objectId, collectionName)
      .then((data) => {
        displayMessage(
          data.message,
          getUserFriendlyMessages(REMOVED, collectionName)
        );
        updateConsultant();
      })
      .catch(displayError);
  };

  return (
    <>
      <Dialog open={addLanguageOpen} onClose={() => setAddLanguageOpen(false)}>
        <DialogTitle>Add profile language</DialogTitle>
        <DialogContent>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              createTranslation(
                languageToAdd,
                seedFromSelected ? selectedLanguage : undefined
              );
            }}
          >
            <div style={{ display: "flex", flexDirection: "column" }}>
              <TextField
                select
                name="language"
                label="Language"
                value={languageToAdd}
                onChange={(e) => setLanguageToAdd(e.target.value)}
                className="form-cotrol"
                variant="outlined"
                style={{ margin: "10px 0px", width: "100%" }}
              >
                {missingLanguages.map((language) => (
                  <MenuItem key={language} value={language}>
                    {getProfileLanguageName(language)}
                  </MenuItem>
                ))}
              </TextField>
              {selectedTranslation && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={seedFromSelected}
                      onChange={() => setSeedFromSelected(!seedFromSelected)}
                      color="primary"
                    />
                  }
                  label={`Start with a copy of the ${getProfileLanguageName(
                    selectedLanguage
                  )} profile`}
                />
              )}
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                margin: "5% 0%",
              }}
            >
              <Button
                type="submit"
                variant="outlined"
                color="primary"
                style={{ margin: "5px" }}
                disabled={languageToAdd === "" || creatingTranslation}
              >
                Create
              </Button>
              <Button
                onClick={() => setAddLanguageOpen(false)}
                variant="outlined"
                color="secondary"
                style={{ margin: "5px" }}
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        isConfirmOpen={deleteLanguageOpen}
        content={`Delete the ${getProfileLanguageName(
          selectedLanguage
        )} profile and all its content?`}
        okText="Delete"
        cancelText="Cancel"
        handleOk={deleteTranslation}
        handleCancel={() => setDeleteLanguageOpen(false)}
      />
      <div
        style={{
          display: "flex",
          alignItems: onMobile ? "flex-start" : "flex-end",
          flexDirection: onMobile ? "column" : "row",
          borderBottom: "1px solid #e1e1e1",
          paddingBottom: "20px",
          position: "relative",
        }}
      >
        <div
          style={{
            width: onMobile ? "100%" : "",
            display: "flex",
            justifyContent: onMobile ? "center" : "flex-end",
            marginBottom: onMobile ? "20px" : "",
          }}
        >
          <div
            style={{
              width: "150px",
              height: "150px",
              position: "relative",
            }}
          >
            <img
              alt="Profile"
              src={getProfilePictureSource(false, userProfilePic)}
              style={{
                objectFit: "cover",
                width: "150px",
                height: "150px",
                borderRadius: "50%",
                boxShadow: "0px 1px 16px -4px rgba(0,0,0,0.75)",
                cursor: "pointer",
                border: " 2px solid #13222D",
                position: "relative",
                top: "0",
                left: "0",
              }}
            />
            <form onSubmit={onSubmit} encType="multipart/form-data">
              <label
                style={{
                  width: "150px",
                  height: "150px",
                  borderRadius: "50%",
                  background: pictureHovered ? "#13222D80" : "transparent",
                  position: "absolute",
                  top: "0",
                  left: "0",
                  paddingTop: "60px",
                  paddingLeft: "62px",
                  color: "white",
                  cursor: "pointer",
                }}
                onMouseOver={() => setPictureHovered(true)}
                onMouseOut={() => {
                  setPictureHovered(false);
                }}
              >
                <EditIcon
                  style={{
                    opacity: pictureHovered ? "100%" : "0%",
                    fontSize: "27px",
                  }}
                />
                <input
                  type="file"
                  accept=".png, .jpg, .jpeg"
                  name="profilePic"
                  onChange={changeProfilePic}
                  style={{ visibility: "hidden" }}
                />
              </label>
            </form>
          </div>
        </div>
        <UserInfo
          userInfo={consultant}
          privateResume={consultant.privateResume || false}
          showPrivateResumeToggle={props.showPrivateResumeToggle}
          canTogglePrivateResume={translations.some(
            (translation) => translation.published
          )}
          onSaveUserInfo={saveUserInfo}
          onChangePrivateResumeStatus={changePrivateResumeStatus}
        />
        {props.changePasswordComponent}
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          borderBottom: "1px solid #e1e1e1",
        }}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          <Tabs
            value={selectedLanguage || false}
            onChange={(_event, language) => setSelectedLanguage(language)}
            indicatorColor="primary"
            textColor="primary"
          >
            {translations.map((translation) => (
              <Tab
                key={translation.language}
                value={translation.language}
                label={getProfileLanguageName(translation.language)}
              />
            ))}
          </Tabs>
          {missingLanguages.length > 0 && (
            <Button color="primary" onClick={openAddLanguage}>
              <AddIcon fontSize="small" style={{ marginRight: "5px" }} />
              Add language
            </Button>
          )}
        </div>
        {selectedTranslation && (
          <div style={{ display: "flex", alignItems: "center" }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedTranslation.published || false}
                  onChange={() =>
                    changePublishedStatus(!selectedTranslation.published)
                  }
                  name="published"
                  color="primary"
                />
              }
              label="Done editing"
              style={{ margin: "0px" }}
            />
            <IconButton
              variant="outlined"
              color="secondary"
              onClick={() => setDeleteLanguageOpen(true)}
            >
              <DeleteIcon />
            </IconButton>
          </div>
        )}
      </div>

      {!selectedTranslation && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            margin: "40px 0px",
          }}
        >
          <p>No profile yet. Create one to get started:</p>
          <div style={{ display: "flex" }}>
            {missingLanguages.map((language) => (
              <Button
                key={language}
                variant="outlined"
                color="primary"
                style={{ margin: "5px" }}
                disabled={creatingTranslation}
                onClick={() => createTranslation(language, undefined)}
              >
                <AddIcon style={{ marginRight: "5px" }} />
                {getProfileLanguageName(language)}
              </Button>
            ))}
          </div>
        </div>
      )}

      {selectedTranslation && (
        // Keyed by language so switching tabs remounts the sections and no
        // editing state (open forms, typed input) leaks between languages.
        <React.Fragment key={selectedLanguage}>
          <div style={{ margin: "15px 0px 0px 0px" }}>
            <ProfileTitle
              title={selectedTranslation.title}
              onSubmitTitle={onSubmitTitle}
            />
          </div>
          <div
            style={{
              display: "flex",
              width: "100%",
              flexDirection: onMobile ? "column" : "row",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                width: !onMobile ? "55%" : "100%",
                paddingRight: !onMobile ? "10px" : "0px",
              }}
            >
              <Presentation
                presentation={selectedTranslation.presentation}
                onSubmitPresentation={onSubmitPresentation}
                profileLanguage={selectedLanguage}
              />
              {onMobile && (
                <>
                  <Roles
                    onMobile={onMobile}
                    onSubmitRole={onSubmitRole}
                    profileLanguage={selectedLanguage}
                    roles={selectedTranslation.roles}
                    handleRemoveRole={handleRemoveRole}
                    updateRole={updateRole}
                  />
                  <Skills
                    onMobile={onMobile}
                    onSubmitSkills={onSubmitSkills}
                    profileLanguage={selectedLanguage}
                    skills={selectedTranslation.skills}
                    handleRemoveSkill={handleRemoveSkill}
                    updateSkill={updateSkill}
                  />
                  <Languages
                    onMobile={onMobile}
                    languages={selectedTranslation.languages}
                    onSubmitLanguages={onSubmitLanguages}
                    profileLanguage={selectedLanguage}
                    handleRemoveLanguage={handleRemoveLanguage}
                    updateLanguage={updateLanguage}
                  />
                  <Hobbies
                    onSubmitHobbies={onSubmitHobbies}
                    profileLanguage={selectedLanguage}
                    handleRemoveHobby={handleRemoveHobby}
                    hobbies={selectedTranslation.hobbies}
                  />
                </>
              )}
              {!onMobile && (
                <Experiences
                  onMobile={onMobile}
                  onSubmitExperiences={onSubmitExperiences}
                  profileLanguage={selectedLanguage}
                  handleRemoveExperience={handleRemoveExperience}
                  updateExperience={updateExperience}
                  experiences={selectedTranslation.experiences}
                />
              )}
            </div>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                width: !onMobile ? "45%" : "100%",
              }}
            >
              {!onMobile && (
                <>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      width: "100%",
                    }}
                  >
                    <Roles
                      onMobile={onMobile}
                      onSubmitRole={onSubmitRole}
                      profileLanguage={selectedLanguage}
                      roles={selectedTranslation.roles}
                      handleRemoveRole={handleRemoveRole}
                      updateRole={updateRole}
                    />
                    <Skills
                      onMobile={onMobile}
                      onSubmitSkills={onSubmitSkills}
                      profileLanguage={selectedLanguage}
                      skills={selectedTranslation.skills}
                      handleRemoveSkill={handleRemoveSkill}
                      updateSkill={updateSkill}
                    />
                    <Languages
                      onMobile={onMobile}
                      languages={selectedTranslation.languages}
                      onSubmitLanguages={onSubmitLanguages}
                      profileLanguage={selectedLanguage}
                      handleRemoveLanguage={handleRemoveLanguage}
                      updateLanguage={updateLanguage}
                    />
                    <Hobbies
                      onSubmitHobbies={onSubmitHobbies}
                      profileLanguage={selectedLanguage}
                      handleRemoveHobby={handleRemoveHobby}
                      hobbies={selectedTranslation.hobbies}
                    />
                  </div>
                </>
              )}
              {onMobile && (
                <Experiences
                  onMobile={onMobile}
                  onSubmitExperiences={onSubmitExperiences}
                  profileLanguage={selectedLanguage}
                  handleRemoveExperience={handleRemoveExperience}
                  updateExperience={updateExperience}
                  experiences={selectedTranslation.experiences}
                />
              )}
              <Educations
                onSubmitEducations={onSubmitEducations}
                profileLanguage={selectedLanguage}
                handleRemoveEducation={handleRemoveEducation}
                educations={selectedTranslation.educations}
                updateEducation={updateEducation}
              />
            </div>
          </div>
        </React.Fragment>
      )}
    </>
  );
};

export default ProfileEditor;
